import { useState, useEffect, useMemo, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { MutantApiService } from "../../../services/MutantApiService";
import QualityEnum from "../../../enums/QualityEnum";

export const providerSupportsPresets = (provider) =>
	Boolean(provider) && !["XTTS", "VITS"].includes((provider || "").toUpperCase());

const useDemoForm = () => {
	const [models, setModels] = useState([]);
	const [defaultProvider, setDefaultProvider] = useState(null);
	const [loadingProviders, setLoadingProviders] = useState(false);
	const [vcSpeakers, setVcSpeakers] = useState([]);
	const [loading, setLoading] = useState(false);
	// loadingProviders state removed - providers are not required anymore
	const [audioUrl, setAudioUrl] = useState(null);
	const [presets, setPresets] = useState([]);
	const [loadingPresets, setLoadingPresets] = useState(false);
	const api = useMemo(() => new MutantApiService(), []);

	const fetchProviders = useCallback(async () => {
		setLoadingProviders(true);
		try {
			const response = await api.makeHttpRequest({ url: `/provider?perPage=100` });
			let providersArr = [];
			if (Array.isArray(response)) providersArr = response;
			else if (Array.isArray(response?.data)) providersArr = response.data;
			else if (Array.isArray(response?.data?.data)) providersArr = response.data.data;

			const defaultProv = providersArr.find((p) => p.default === true) ?? null;
			const defaultName = defaultProv ? (defaultProv.name ?? null) : null;
			setDefaultProvider(defaultName);
			return defaultName;
		} catch (error) {
			console.error("Error fetching providers:", error);
			setDefaultProvider(null);
			return null;
		} finally {
			setLoadingProviders(false);
		}
	}, [api]);

	const fetchModels = useCallback(
		async (provider, defaultProviderOverride = null) => {
			try {
				const url = provider ? `/models?statusFilter=active` : `/models?statusFilter=active&perPage=1000`;
				console.log("Fetching models", provider ? `for provider: ${provider}` : "(all)");
				const response = await api.makeHttpRequest({
					url,
				});

				let modelsArr = [];
				if (Array.isArray(response)) modelsArr = response;
				else if (Array.isArray(response?.data)) modelsArr = response.data;
				else if (Array.isArray(response?.data?.data)) modelsArr = response.data.data;

				if (!provider) {
					const usedDefault = (defaultProviderOverride ?? defaultProvider ?? "").toUpperCase();
					if (!usedDefault) {
						modelsArr = modelsArr.filter((m) => ((m.provider || "") + "").toUpperCase() === "ELEVEN_LABS");
					} else {
						modelsArr = modelsArr.filter((m) => {
							const p = ((m.provider || "") + "").toUpperCase();
							return p === usedDefault || p === "ELEVEN_LABS";
						});
					}
				}

				setModels(modelsArr);
			} catch (error) {
				console.error("Error fetching models:", error);
				setModels([]);
			}
		},
		[api, defaultProvider]
	);
	const fetchVcSpeakers = useCallback(async (provider) => {
		setVcSpeakers([]);
		return;
	}, [api]);




	useEffect(() => {
		let canceled = false;
		const load = async () => {
			try {
				const def = await fetchProviders();
				if (canceled) return;
				await fetchModels(null, def);
			} catch (e) {
				console.error("Error loading providers/models:", e);
			}
		};
		load();
		return () => {
			canceled = true;
		};
	}, [api, fetchProviders, fetchModels]);

	const formik = useFormik({
		initialValues: {
			model: null,
			compression: null,
			audioFormat: "WAV",
			text: "",
			vcSpeaker: null,
			preset: null,
		},
		validationSchema: Yup.object({
			model: Yup.string().nullable().required("Escolha um modelo"),
			compression: Yup.string().oneOf(Object.values(QualityEnum)).nullable().required("Escolha um tipo de compressão"),
			audioFormat: Yup.string().oneOf(["WAV", "MP3", "OGG"]).required("Escolha um formato"),
			text: Yup.string().trim().required("Insira um texto para ser processado"),
			vcSpeaker: Yup.string().nullable(),
			// provider removed - we derive provider from selected model
			preset: Yup.string().nullable()
		}),
		validateOnChange: true,
		validateOnBlur: true,
		onSubmit: (values, { setSubmitting, setFieldError }) => {
			console.log("onSubmit EXECUTADO! Form submitted with values:", values); // Debug log to check if onSubmit is called
			setLoading(true);
			setAudioUrl(null);
			api.makeHttpRequest({
				method: "POST",
				url: "/prompts/demo",
				data: {
					speaker: values.model,
					compression: values.compression,
					text: values.text,
					audioFormat: values.audioFormat,
					vcSpeaker: values.vcSpeaker,
					provider: models.find(m => (m.name ?? m.model ?? "") === values.model)?.provider ?? null,
					presetId: values.preset,
				},
				timeout: 120000,
			})
				.then((response) => {
					console.log("Resposta da API recebida:", response);

					try {
						let audioBase64 = response;

						if (typeof audioBase64 === 'object' && audioBase64 !== null) {
							audioBase64 = JSON.stringify(audioBase64);
						}

						if (typeof audioBase64 !== 'string') {
							throw new Error('Resposta não é uma string base64 válida');
						}

						console.log("Base64 length:", audioBase64.length);

						const decodedAudio = atob(audioBase64);
						const buffer = new ArrayBuffer(decodedAudio.length);
						const view = new Uint8Array(buffer);
						for (let i = 0; i < decodedAudio.length; i++) {
							view[i] = decodedAudio.charCodeAt(i);
						}

						const mimeType = values.audioFormat.toLowerCase();
						const finalMimeType = mimeType === 'ogg' ? 'audio/ogg' : `audio/${mimeType}`;

						const blob = new Blob([buffer], {
							type: finalMimeType,
						});
						const url = URL.createObjectURL(blob);
						console.log("URL do áudio criada:", url);
						console.log("Blob type:", blob.type, "Size:", blob.size);
						setAudioUrl(url);
					} catch (error) {
						console.error("Erro ao processar áudio:", error);
						setFieldError("general", "Erro ao processar áudio: " + error.message);
					}
				})
				.catch((error) => {
					console.error("Error processing text", error);
					setFieldError("general", "Erro ao processar texto");
				})
				.finally(() => {
					setLoading(false);
					setSubmitting(false);
				});
		},
	});
	const selectedModelName = formik.values.model;
	const setFieldValue = formik.setFieldValue;

	useEffect(() => {
		let canceled = false;
		// derive provider from selected model name (value comes from outer scope)
		const provider = models.find(m => (m.name ?? m.model ?? "") === selectedModelName)?.provider ?? null;
		console.log("[useDemoForm] derived provider:", provider, "selectedModelName:", selectedModelName);
		if (providerSupportsPresets(provider)) {
			const loadPresets = async () => {
				setLoadingPresets(true);
				try {
					const response = await api.makeHttpRequest({
						url: "/presets?perPage=100",
					});
					if (!canceled) {
						const presetsArr = Array.isArray(response)
							? response
							: Array.isArray(response?.data)
								? response.data
								: [];
						console.log("[useDemoForm] fetched presets count:", presetsArr.length);
						setPresets(presetsArr);
					}
				} catch (error) {
					console.error("Error fetching presets:", error);
					if (!canceled) {
						setPresets([]);
					}
				} finally {
					if (!canceled) {
						setLoadingPresets(false);
					}
				}
			};
			loadPresets();
		} else {
			setPresets([]);
			setLoadingPresets(false);
			if (selectedModelName !== null && setFieldValue) {
				setFieldValue("preset", null);
			}
		}

		return () => {
			canceled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [api, selectedModelName, setFieldValue, models]);

	return {
		formik,
		models,
		vcSpeakers,
		loading,
		// loadingProviders,
		audioUrl,
		presets,
		loadingPresets,
		fetchVcSpeakers,
		fetchModels,
	};
};

export default useDemoForm;